import React, { Fragment, useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { Portal } from 'react-portal';
import * as styles from './styles.module.css';

const getWidth = () => {
  if (typeof window !== 'undefined') {
    return (
      window.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth
    );
  }
};

const getFocusableElements = (element) => {
  return element.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
};

export default function NavBar({
    isKeyboardNav,
  mobileBreakPoint,
  mobileMenuOverlayBG,
  triggerHamburgerClose,
  children,
  ...props
}) {
  const [isHamburgerActive, setIsHamburgerActive] = useState(false);
  const [width, setWidth] = useState(getWidth());
  const [isMobile, setIsMobile] = useState(false);
  const [startMenuAnim, setStartMenuAnim] = useState(false);
  const menuRef = useRef(null);
  const hamburgerIconRef = useRef(null);
  const [pageLoaded, setPageLoaded] = useState(false);

  useEffect(() => {
    setPageLoaded(true);
  }, [])

  const onHamburgerClick = () => {
    setIsHamburgerActive(prevState => !prevState);
  };

  const closeHamburgerMenu = () => {
    setIsHamburgerActive(false);
  };

  const handleKeyDown = useCallback((event) => {
    console.log(`Key Down on element: ${event.target.tagName}`);
    if (event.key === 'Escape' && isHamburgerActive) {
      closeHamburgerMenu();
    }
  }, [isHamburgerActive]);

  const trapFocus = useCallback(
    (event) => {

        console.log(`Focus trap for: ${event.target.tagName}`);

      if (isHamburgerActive && menuRef.current) {
        const focusableElements = getFocusableElements(menuRef.current);
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
          lastElement.focus();
          event.preventDefault();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          firstElement.focus();
          event.preventDefault();
        }
      }
    },
    [isHamburgerActive]
  );

  useEffect(() => {
    const currentMenuRef = menuRef.current;
    
    if (currentMenuRef) {
      currentMenuRef.addEventListener('keydown', handleKeyDown);
      return () => {
        currentMenuRef.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [handleKeyDown]);

  useEffect(() => {
    triggerHamburgerClose && setIsHamburgerActive(false);
  }, [triggerHamburgerClose]);

  useEffect(() => {
    const resizeListener = () => {
      setWidth(getWidth());
    };
    window.addEventListener('resize', resizeListener);
    return () => {
      window.removeEventListener('resize', resizeListener);
    };
  }, []);

  useLayoutEffect(() => {
    if (width <= mobileBreakPoint) {
      setIsMobile(true);
    } else {
      setIsHamburgerActive(false);
      setIsMobile(false);
    }
  }, [width]);

  useEffect(() => {
    isHamburgerActive
      ? setTimeout(() => {
        setStartMenuAnim(true);
      }, 50)
      : setStartMenuAnim(false);
  }, [isHamburgerActive]);

  useEffect(() => {
    window.addEventListener('keydown', trapFocus);
    return () => {
      window.removeEventListener('keydown', trapFocus);
    };
  }, [trapFocus]);



  useEffect(() => {
    if (isHamburgerActive) { // Check if the hamburger menu is active
      setTimeout(() => {
        // Use a more specific selector to target links within the nav with "Main menu" aria-label
        const menuItem = document.querySelector('nav[aria-label="Main menu"] a[href="/overdose-crisis/"]');
        if (menuItem) {
          menuItem.focus(); // Set focus on the specific menu item
        }
      }, 600); // Adjust based on your menu's animation duration
    }
  }, [isHamburgerActive]);



  return (
    <nav
      role="navigation"
      aria-label="Main Navigation"
      ref={menuRef}
      className={`${styles.navBar} nav-bar ${isKeyboardNav ? 'keyboard-nav' : ''}`}
      {...props}
    >
      {/* Render for Desktop */}
      {!isMobile && pageLoaded && (
        <div className={`desktop ${styles.navContent} nav-content`}>
          {children}
        </div>
      )}

      {/* Render for Mobile */}
      {isMobile && pageLoaded && (
        <Fragment>
          {isHamburgerActive && (
            <Portal>
              <div
                className={`mobile ${styles.overlayBackground} ${mobileMenuOverlayBG} ${styles.overlay}`}
                role="dialog"
                aria-modal="true"
                aria-hidden={!isHamburgerActive}
              >
                <div
                  className={`${styles.mobileMenuContent} ${startMenuAnim && styles.mobileMenuContentActive}`}
                >
                  {React.Children.map(children, (child, index) => {
                    if (React.isValidElement(child)) {
                      return (
                        <Fragment>
                          {React.cloneElement(child, { className: "mainNav", closeHamburgerMenu })}
                          {/* Assuming sub-nav items are children of the main nav item */}
                          {child.props.children && (
                            <div className="subNav">
                              {child.props.children}
                            </div>
                          )}
                        </Fragment>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            </Portal>
          )}
          {/* Hamburger icon */}
          <div
            ref={hamburgerIconRef} // Bind the ref here
            role="button"
            tabIndex={0}
            aria-label="Toggle Menu"
            aria-pressed={isHamburgerActive ? "true" : "false"}
            className={`${styles.hamburgerIcon} ${isHamburgerActive && styles.hamburgerIconActive} ${isKeyboardNav ? 'focusVisible' : ''}`}
            onClick={onHamburgerClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                onHamburgerClick();
              }
            }}
          >
            <div />
          </div>
        </Fragment>
      )}
    </nav>
  );
}

NavBar.propTypes = {
  mobileBreakPoint: PropTypes.number.isRequired,
  mobileMenuOverlayBG: PropTypes.string,
  triggerHamburgerClose: PropTypes.bool,
  children: PropTypes.node.isRequired,
  isKeyboardNav: PropTypes.bool,
};
